import React, { useState } from "react";
import api from "./services/api";

interface BoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
}

interface DetectionResult {
  id: string;
  class_name: string;
  score: number;
  bbox: BoundingBox;
}

interface GeneratedImage {
  id: string;
  prompt: string;
  imageUrl: string;
  localPath?: string;
  exclude: boolean;
  detectionResults?: DetectionResult[];
  uploaded?: boolean;
  createdAvatarId?: string;
}

export default function BatchAddCharacterPage() {
  const [prompts, setPrompts] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  const [isCreatingAvatars, setIsCreatingAvatars] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [error, setError] = useState<string>("");

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompts(e.target.value);
  };

  const generateImages = async () => {
    if (!prompts.trim()) {
      setError("Please enter at least one prompt");
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setError("");

    try {
      // Split prompts by new line
      const promptList = prompts
        .split("\n")
        .filter((prompt) => prompt.trim().length > 0);

      if (promptList.length === 0) {
        throw new Error("No valid prompts found");
      }

      // Update progress for starting process
      setProgress(10);

      // Create an array of promises, one for each prompt
      const imagePromises = promptList.map((prompt, i) => {
        return api.post("/api/characters/generate-images", {
          prompt: prompt,
        })
        .then(response => {
          if (!response.data || !response.data.success) {
            console.error(`Failed to generate image for prompt: ${prompt}`);
            return [];
          }

          // Extract local paths if available
          const localPaths = response.data.local_paths || [];

          // Return array of generated images for this prompt
          return response.data.images.map((base64: string, index: number) => ({
            id: `${i}-${index}`,
            prompt: prompt,
            imageUrl: `data:image/png;base64,${base64}`,
            localPath: localPaths[index] || null,
            exclude: false,
          }));
        })
        .catch(err => {
          console.error(`Error generating images for prompt: ${prompt}`, err);
          return []; // Return empty array in case of error
        });
      });

      // Set progress to indicate API calls are in progress
      setProgress(30);

      // Wait for all API calls to complete in parallel
      const resultsArrays = await Promise.all(imagePromises);
      
      // Flatten the array of arrays into a single array of images
      const results = resultsArrays.flat();

      // Set the generated images
      setGeneratedImages(results);
      setProgress(100);
    } catch (err) {
      console.error("Error in batch processing:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleExclude = (id: string) => {
    setGeneratedImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, exclude: !img.exclude } : img
      )
    );
  };

  const runObjectDetection = async () => {
    const includedImages = generatedImages.filter((img) => !img.exclude);

    if (includedImages.length === 0) {
      setError("Please include at least one image");
      return;
    }

    setIsDetecting(true);
    setError("");
    setProgress(0);

    try {
      // Update progress to indicate starting process
      setProgress(10);
      
      // Create a map to lookup image indices in the original array
      const imageIndexMap: Record<string, number> = {};
      generatedImages.forEach((img, index) => {
        imageIndexMap[img.id] = index;
      });
      
      // Create detection promises for each valid image
      const detectionPromises = includedImages
        .filter(image => image.localPath) // Only include images with a local path
        .map(image => {
          console.log(`Running object detection on image: ${image.localPath}`);
          
          return api.post("/api/characters/object-detection", {
            image_url: image.localPath,
          })
          .then(detectionResponse => {
            if (!detectionResponse.data || !detectionResponse.data.success) {
              console.error(`Failed to detect objects in image ${image.id}`);
              return { imageId: image.id, success: false, data: null };
            }
            
            console.log(
              `Detection results for image ${image.id}:`,
              detectionResponse.data.predictions
            );
            
            return {
              imageId: image.id,
              success: true,
              data: {
                detectionResults: detectionResponse.data.predictions,
                uploaded: true,
                imageUrl: detectionResponse.data.uploaded_image_url || image.imageUrl
              }
            };
          })
          .catch(err => {
            console.error(`Error detecting objects in image ${image.id}:`, err);
            return { imageId: image.id, success: false, data: null };
          });
        });
      
      // Update progress to indicate API calls are in progress
      setProgress(30);
      
      // Wait for all API calls to complete in parallel
      const results = await Promise.all(detectionPromises);
      
      // Update the images with detection results
      const updatedImages = [...generatedImages];
      
      // Apply the detection results to the correct images
      results.forEach(result => {
        if (result.success && result.data) {
          const index = imageIndexMap[result.imageId];
          if (index !== undefined) {
            updatedImages[index] = {
              ...updatedImages[index],
              ...result.data
            };
          }
        }
      });
      
      // Update state with detection results
      setGeneratedImages(updatedImages);
      setProgress(100);
    } catch (err) {
      console.error("Error in object detection:", err);
      setError(err instanceof Error ? err.message : "Object detection failed");
    } finally {
      setIsDetecting(false);
    }
  };

  const createAvatars = async () => {
    const imagesToProcess = generatedImages.filter(
      (img) => !img.exclude
    );

    if (imagesToProcess.length === 0) {
      setError("No images to process");
      return;
    }

    setIsCreatingAvatars(true);
    setError("");
    setProgress(0);

    try {
      // Update progress to indicate starting process
      setProgress(10);

      // Create a map to lookup image indices in the original array
      const imageIndexMap: Record<string, number> = {};
      generatedImages.forEach((img, index) => {
        imageIndexMap[img.id] = index;
      });
      
      // Create request data array for all images at once
      const requestDataArray = imagesToProcess.map(image => {
        // Use default values if no detection results
        let bbox = { x1: 0, y1: 0, width: 100, height: 100 };
        let className = "Default";
        let score = 1.0;
        
        // Use detected values if available
        if (image.detectionResults && image.detectionResults.length > 0) {
          const detection = image.detectionResults[0];
          bbox = detection.bbox;
          className = detection.class_name;
          score = detection.score;
        }

        // Determine item type based on the prompt
        const isTalkingVideo =
          image.prompt.toLowerCase().includes("talking") ||
          !image.prompt.toLowerCase().includes("holding");
        const item_type = isTalkingVideo ? "talking" : "product";
        
        return {
          imageId: image.id,
          data: {
            name: `${className} ${Math.round(score * 100)}%`,
            prompt: image.prompt,
            values: {}, // Empty values as requested
            character_image_url: image.imageUrl,
            mask_image_url: "", // Empty mask as requested
            x: bbox.x1,
            y: bbox.y1,
            width: bbox.width,
            height: bbox.height,
            item_type: item_type,
          }
        };
      });
      
      console.log(`Processing ${requestDataArray.length} images in parallel`);
      
      // Create all avatars in parallel batches
      const BATCH_SIZE = 5; // Process 5 requests in parallel at a time
      const batches = [];
      
      for (let i = 0; i < requestDataArray.length; i += BATCH_SIZE) {
        batches.push(requestDataArray.slice(i, i + BATCH_SIZE));
      }
      
      // Update progress to indicate batching is complete
      setProgress(20);
      
      // Process each batch sequentially
      const results = [];
      
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const batchPromises = batch.map(item => {
          console.log(`Creating avatar for image ${item.imageId}`);
          
          return api.post("/api/characters", item.data)
            .then(avatarResponse => {
              if (avatarResponse.data && avatarResponse.data.avatar) {
                console.log(
                  `Successfully created avatar ${avatarResponse.data.avatar.id} for image ${item.imageId}`
                );
                
                return {
                  imageId: item.imageId,
                  success: true,
                  createdAvatarId: avatarResponse.data.avatar.id
                };
              }
              return { imageId: item.imageId, success: false, createdAvatarId: null };
            })
            .catch(err => {
              console.error(`Error creating avatar for image ${item.imageId}:`, err);
              return { imageId: item.imageId, success: false, createdAvatarId: null };
            });
        });
        
        // Process this batch in parallel
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
        
        // Update progress based on completed batches
        const progressPercent = 20 + Math.floor(((i + 1) / batches.length) * 60);
        setProgress(progressPercent);
      }

      // Update the images with avatar creation results
      const updatedImages = [...generatedImages];

      // Apply the avatar results to the correct images
      results.forEach(result => {
        if (result.success && result.createdAvatarId) {
          const index = imageIndexMap[result.imageId];
          if (index !== undefined) {
            updatedImages[index] = {
              ...updatedImages[index],
              createdAvatarId: result.createdAvatarId
            };
          }
        }
      });

      // Update state with created avatars
      setGeneratedImages(updatedImages);

      // Count successful avatar creations
      const createdCount = results.filter(r => r.success && r.createdAvatarId).length;
      setError(`Successfully created ${createdCount} avatars!`);
      setProgress(100);
    } catch (err) {
      console.error("Error creating avatars:", err);
      setError(err instanceof Error ? err.message : "Failed to create avatars");
    } finally {
      setIsCreatingAvatars(false);
    }
  };

  const getSuccessCount = () => {
    return generatedImages.filter((img) => img.createdAvatarId).length;
  };

  const getPendingCount = () => {
    return generatedImages.filter(
      (img) => !img.exclude && !img.createdAvatarId
    ).length;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Batch Add Characters</h1>

      {error && (
        <div
          className={`border px-4 py-3 rounded relative mb-4 ${
            error.includes("Successfully created")
              ? "bg-green-100 border-green-400 text-green-700"
              : "bg-red-100 border-red-400 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Enter prompts (one per line)
        </label>
        <textarea
          value={prompts}
          onChange={handlePromptChange}
          rows={8}
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter multiple prompts, one per line..."
          disabled={isGenerating}
        />
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={generateImages}
          disabled={isGenerating || !prompts.trim()}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          {isGenerating ? "Generating..." : "Generate Images"}
        </button>

        {generatedImages.length > 0 && (
          <button
            onClick={runObjectDetection}
            disabled={
              isDetecting ||
              generatedImages.filter((img) => !img.exclude).length === 0
            }
            className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          >
            {isDetecting ? "Detecting Objects..." : "Run Object Detection"}
          </button>
        )}

        {generatedImages.length > 0 && (
          <button
            onClick={createAvatars}
            disabled={isCreatingAvatars || generatedImages.filter(img => !img.exclude).length === 0}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
          >
            {isCreatingAvatars
              ? "Creating Avatars..."
              : `Create Avatars (${generatedImages.filter(img => !img.exclude).length})`}
          </button>
        )}
      </div>

      {/* Progress bar */}
      {(isGenerating || isDetecting || isCreatingAvatars) && (
        <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      {/* Status summary */}
      {generatedImages.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-100 p-4 rounded-lg text-center">
            <span className="text-xl font-bold text-blue-800">
              {generatedImages.length}
            </span>
            <p className="text-blue-600">Images Generated</p>
          </div>
          <div className="bg-purple-100 p-4 rounded-lg text-center">
            <span className="text-xl font-bold text-purple-800">
              {
                generatedImages.filter(
                  (img) =>
                    img.detectionResults && img.detectionResults.length > 0
                ).length
              }
            </span>
            <p className="text-purple-600">Objects Detected</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg text-center">
            <span className="text-xl font-bold text-green-800">
              {getSuccessCount()}
            </span>
            <p className="text-green-600">Avatars Created</p>
          </div>
        </div>
      )}

      {/* Generated images */}
      {generatedImages.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            Generated Images (Click to exclude)
          </h2>
          <p className="mb-4 text-gray-600">
            Click on images you want to <strong>exclude</strong> from being
            processed.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {generatedImages.map((image) => (
              <div
                key={image.id}
                onClick={() => toggleExclude(image.id)}
                className={`
                  relative border-4 rounded-lg cursor-pointer transition-all
                  ${
                    image.exclude
                      ? "border-red-500 opacity-50"
                      : image.createdAvatarId
                      ? "border-green-500"
                      : image.detectionResults &&
                        image.detectionResults.length > 0
                      ? "border-purple-500"
                      : "border-transparent hover:border-gray-300"
                  }
                `}
              >
                <img
                  src={image.imageUrl}
                  alt={`Generated for: ${image.prompt.substring(0, 20)}...`}
                  className="w-full h-auto rounded-lg"
                />

                {/* Detection results overlay */}
                {image.detectionResults &&
                  image.detectionResults.length > 0 &&
                  !image.exclude && (
                    <svg
                      className="absolute inset-0 w-full h-full"
                      style={{ pointerEvents: "none" }}
                    >
                      {image.detectionResults.map((detection, idx) => (
                        <rect
                          key={idx}
                          x={detection.bbox.x1}
                          y={detection.bbox.y1}
                          width={detection.bbox.width}
                          height={detection.bbox.height}
                          stroke="rgba(147, 51, 234, 0.8)" // Purple
                          strokeWidth="3"
                          fill="rgba(147, 51, 234, 0.2)"
                        />
                      ))}
                    </svg>
                  )}

                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-2 text-sm truncate">
                  {image.prompt.length > 30
                    ? `${image.prompt.substring(0, 30)}...`
                    : image.prompt}
                </div>

                {image.exclude && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-red-500 text-4xl font-bold">✕</span>
                  </div>
                )}

                {/* Status badges */}
                <div className="absolute top-2 right-2 flex flex-col gap-1">
                  {image.detectionResults &&
                    image.detectionResults.length > 0 && (
                      <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded">
                        Detected: {image.detectionResults[0].class_name}
                      </span>
                    )}
                  {image.createdAvatarId && (
                    <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                      Avatar Created
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
